import { ThreeEvent } from '@react-three/fiber';
import { FunctionComponent, useMemo } from 'react';
import { BoxGeometry, PlaneGeometry } from 'three';
import { maths } from '../../../utils';
import { ITuple2, ITuple3, ITuple4 } from '../../r3f/r3f-components/component-data-structure';
import { useSRGBColors } from '../r3f-components/hooks/useSRGBColors';

interface IParentProps {
	enabled?: boolean;
	visible?: boolean;
	rotation?: ITuple3;
	position?: ITuple3;
	scale: ITuple2 | ITuple3;
	color: ITuple4;
	renderOrder?: number;
	lineWidth?: number;
	stopEventPropagation?: boolean;
	pointerDownHandler?: (e: ThreeEvent<PointerEvent>) => unknown;
	pointerUpHandler?: (e: ThreeEvent<PointerEvent>) => unknown;
}

const LineBox: FunctionComponent<IParentProps> = ({
	visible,
	rotation: [rx, ry, rz] = [0, 0, 0],
	position = [0, 0, 0],
	scale: [sx, sy, sz],
	enabled = true,
	color: c,
	lineWidth = 4,
	pointerDownHandler,
	pointerUpHandler,
	renderOrder,
	stopEventPropagation: stpEnvProp
}) => {
	const geom = useMemo(() => {
        return !!sz ? 
            new BoxGeometry(sx * 2, sy * 2, sz * 2) :
            new PlaneGeometry(sx * 2, sy * 2);
    }, [sx, sy, sz]);

	const geometry = useMemo(() => <edgesGeometry attach="geometry" args={[geom]} />, [geom]);
	const rotation = [maths.toRadians(rx), maths.toRadians(ry), maths.toRadians(rz)] as any;

	const color = useSRGBColors(c);

	if (renderOrder === undefined) renderOrder = 999995;

	return (
		<group renderOrder={renderOrder}>
			<mesh
				renderOrder={renderOrder}
				position={position as any}
				rotation={rotation}
				visible={visible}
				onPointerDown={(e) => {
					if (stpEnvProp) e.stopPropagation();
					if (enabled && pointerDownHandler) pointerDownHandler(e);
				}}
				onPointerUp={(e) => {
					if (stpEnvProp) e.stopPropagation();
					if (enabled && pointerUpHandler) pointerUpHandler(e);
				}}
			>
				<lineSegments>
					{geometry}
					<lineBasicMaterial attach="material" linewidth={lineWidth} opacity={c[3]} color={color} />
				</lineSegments>
			</mesh>
		</group>
	);
};

export default LineBox;
